import * as THREE from 'three';
import type {
  CoinPusherMachine,
  CoinPusherMachineOptions,
  CoinPusherMachineRuntime,
  MachineColliderMetadata,
} from './types';

const DEFAULT_CABINET_ART_URL = `${import.meta.env.BASE_URL}assets/textures/flamingo-carnival-cabinet.png`;
const Y_AXIS = new THREE.Vector3(0, 1, 0);

type ResourceState = {
  geometries: Set<THREE.BufferGeometry>;
  materials: Set<THREE.Material>;
  textures: Set<THREE.Texture>;
  disposed: boolean;
};

type MachineMaterials = {
  blackPaint: THREE.MeshPhysicalMaterial;
  chrome: THREE.MeshPhysicalMaterial;
  brushedSteel: THREE.MeshPhysicalMaterial;
  glass: THREE.MeshPhysicalMaterial;
  cabinetArt: THREE.MeshPhysicalMaterial;
  sideArt: THREE.MeshPhysicalMaterial;
  marquee: THREE.MeshPhysicalMaterial;
  playfield: THREE.MeshPhysicalMaterial;
  nickel: THREE.MeshPhysicalMaterial;
  brass: THREE.MeshPhysicalMaterial;
  prize: THREE.MeshPhysicalMaterial[];
  bulbWarm: THREE.MeshStandardMaterial;
  bulbPink: THREE.MeshStandardMaterial;
  bulbCyan: THREE.MeshStandardMaterial;
  rubber: THREE.MeshStandardMaterial;
  darkInterior: THREE.MeshStandardMaterial;
  glowRed: THREE.MeshStandardMaterial;
  glowCyan: THREE.MeshStandardMaterial;
};

type BuildContext = {
  root: CoinPusherMachine;
  nodes: Record<string, THREE.Object3D>;
  meshes: Record<string, THREE.Mesh | THREE.InstancedMesh>;
  sockets: Record<string, THREE.Object3D>;
  state: ResourceState;
  materials: MachineMaterials;
  seed: number;
  detailMultiplier: number;
};

const resourceStates = new WeakMap<THREE.Object3D, ResourceState>();

export function createCoinPusherMachine(options: CoinPusherMachineOptions = {}): CoinPusherMachine {
  const root = new THREE.Group() as CoinPusherMachine;
  root.name = 'coinPusher.machine.root';

  const state: ResourceState = {
    geometries: new Set(),
    materials: new Set(),
    textures: new Set(),
    disposed: false,
  };
  resourceStates.set(root, state);

  const nodes: Record<string, THREE.Object3D> = { root };
  const meshes: Record<string, THREE.Mesh | THREE.InstancedMesh> = {};
  const sockets: Record<string, THREE.Object3D> = {};
  const detailMultiplier = options.detailLevel === 'performance' ? 0.62 : 1;
  const materials = createMaterials(state, options.detailLevel ?? 'full');
  const context: BuildContext = {
    root,
    nodes,
    meshes,
    sockets,
    state,
    materials,
    seed: options.seed ?? 0x51e57a,
    detailMultiplier,
  };

  buildRunningBase(context);
  buildLowerCabinet(context, options);
  buildPlayfield(context, options.coinCountScale ?? 1);
  buildGlazingAndFrame(context);
  buildMarquee(context);
  buildBulbRibbons(context);

  const pusher = nodes['pusher.pivot'];
  if (!pusher) throw new Error('Coin pusher model is missing its pusher pivot.');

  const colliders: MachineColliderMetadata[] = [
    {
      id: 'cabinet-base-solid',
      shape: 'box',
      center: [0, 1.04, -0.02],
      halfExtents: [2.5, 0.82, 1.55],
      isTrigger: false,
      nodeId: 'cabinet.lower',
      event: 'machine:solid',
    },
    {
      id: 'cabinet-back-solid',
      shape: 'box',
      center: [0, 3.18, -1.27],
      halfExtents: [1.95, 1.48, 0.16],
      isTrigger: false,
      nodeId: 'cabinet.glassBay',
      event: 'machine:solid',
    },
    {
      id: 'playfield-fixed-solid',
      shape: 'box',
      center: [0, 2.1, 0.41],
      halfExtents: [1.87, 0.08, 0.49],
      isTrigger: false,
      nodeId: 'playfield.fixedBed',
      event: 'machine:solid',
    },
    {
      id: 'pusher-moving-solid',
      shape: 'box',
      center: [0, 0, 0],
      halfExtents: [1.82, 0.09, 0.26],
      isTrigger: false,
      nodeId: 'pusher.pivot',
      event: 'machine:solid',
      notes: 'Center is local to pusher.pivot and follows its local +Z stroke.',
    },
    {
      id: 'tray-collection-trigger',
      shape: 'box',
      center: [0, 0.96, 1.72],
      halfExtents: [0.76, 0.2, 0.28],
      isTrigger: true,
      nodeId: 'tray.collection',
      event: 'coin:collect',
    },
    {
      id: 'left-loss-trigger',
      shape: 'box',
      center: [-1.84, 2.18, 0.14],
      halfExtents: [0.16, 0.16, 0.59],
      isTrigger: true,
      nodeId: 'socket.loss.left',
      event: 'coin:lost',
    },
    {
      id: 'right-loss-trigger',
      shape: 'box',
      center: [1.84, 2.18, 0.14],
      halfExtents: [0.16, 0.16, 0.59],
      isTrigger: true,
      nodeId: 'socket.loss.right',
      event: 'coin:lost',
    },
    {
      id: 'front-overflow-trigger',
      shape: 'box',
      center: [0, 1.86, 0.98],
      halfExtents: [1.78, 0.12, 0.16],
      isTrigger: true,
      nodeId: 'socket.payout.edge',
      event: 'coin:collect',
      notes: 'Coins crossing this edge can be routed into the visible tray animation.',
    },
  ];

  const cabinetArtUrl = options.cabinetArtTexture
    ? null
    : options.cabinetArtUrl === undefined
      ? DEFAULT_CABINET_ART_URL
      : options.cabinetArtUrl;

  const runtime: CoinPusherMachineRuntime = {
    version: 1,
    coordinateFrame: {
      front: '+Z',
      up: '+Y',
      origin: 'footprint-center',
    },
    bounds: {
      width: 5.2,
      height: 5.8,
      depth: 3.5,
    },
    nodes,
    meshes,
    sockets,
    colliders,
    destructionGroups: {
      'cabinet-shell': ['cabinet.lower', 'base.plinth', 'cabinet.serviceDoor'],
      glazing: ['glass.front', 'glass.left', 'glass.right'],
      marquee: ['marquee.body', 'marquee.face', 'bulbs.top'],
      playfield: ['playfield.fixedBed', 'pusher.pivot', 'playfield.backboard'],
      'control-pods': ['controls.left', 'controls.right'],
    },
    materials: {
      'black-painted-metal': materials.blackPaint,
      chrome: materials.chrome,
      'brushed-steel': materials.brushedSteel,
      glass: materials.glass,
      'cabinet-art': materials.cabinetArt,
      'side-art': materials.sideArt,
      'marquee-print': materials.marquee,
      'playfield-print': materials.playfield,
      nickel: materials.nickel,
      brass: materials.brass,
      rubber: materials.rubber,
      'bulb-warm': materials.bulbWarm,
      'bulb-pink': materials.bulbPink,
      'bulb-cyan': materials.bulbCyan,
    },
    pusher: {
      nodeId: 'pusher.pivot',
      axis: [0, 0, 1],
      minimum: -0.32,
      maximum: 0.02,
      rest: -0.15,
      phase: 0.5,
    },
    suppliedCabinetArtUrl: cabinetArtUrl,
  };

  root.userData.sculptRuntime = runtime;
  root.userData.assetKind = 'procedural-coin-pusher-machine';
  root.userData.referenceBasis = 'machine-reference.png + interior-reference.png';
  root.userData.fidelity = 'stylized real-time reconstruction; unseen rear mechanics are inferred';

  setCoinPusherPhase(root, options.pusherPhase ?? 0.5);
  setCoinPusherBulbsEnabled(root, options.bulbsOn ?? true);
  applyCabinetArt(root, options, cabinetArtUrl);

  return root;
}

export function getCoinPusherMachineRuntime(root: THREE.Object3D): CoinPusherMachineRuntime {
  const runtime = root.userData.sculptRuntime as CoinPusherMachineRuntime | undefined;
  if (!runtime || runtime.version !== 1) {
    throw new Error('Object is not a compatible procedural coin-pusher machine.');
  }
  return runtime;
}

export function setCoinPusherPhase(root: THREE.Object3D, phase: number): void {
  const runtime = getCoinPusherMachineRuntime(root);
  const normalizedPhase = THREE.MathUtils.clamp(phase, 0, 1);
  const pusher = runtime.nodes[runtime.pusher.nodeId];
  if (!pusher) throw new Error(`Missing runtime node: ${runtime.pusher.nodeId}`);
  pusher.position.z = THREE.MathUtils.lerp(runtime.pusher.minimum, runtime.pusher.maximum, normalizedPhase);
  runtime.pusher.phase = normalizedPhase;
}

export function setCoinPusherBulbsEnabled(root: THREE.Object3D, enabled: boolean): void {
  const runtime = getCoinPusherMachineRuntime(root);
  const levels: Record<string, number> = {
    'bulb-warm': enabled ? 2.3 : 0.08,
    'bulb-pink': enabled ? 2.05 : 0.08,
    'bulb-cyan': enabled ? 1.8 : 0.06,
  };

  for (const [materialId, intensity] of Object.entries(levels)) {
    const material = runtime.materials[materialId] as THREE.MeshStandardMaterial | undefined;
    if (material) material.emissiveIntensity = intensity;
  }
}

export function disposeCoinPusherMachine(root: THREE.Object3D): void {
  const state = resourceStates.get(root);
  if (!state || state.disposed) return;
  state.disposed = true;
  for (const geometry of state.geometries) geometry.dispose();
  for (const material of state.materials) material.dispose();
  for (const texture of state.textures) texture.dispose();
  state.geometries.clear();
  state.materials.clear();
  state.textures.clear();
  resourceStates.delete(root);
}

function createMaterials(state: ResourceState, detailLevel: 'full' | 'performance'): MachineMaterials {
  const mapSize = detailLevel === 'full' ? 256 : 128;
  const paintMap = createPaintTexture(state, 1024, 0x7b151f);
  const marqueeMap = createMarqueeTexture(state, 1024, 256);
  const cabinetFallback = createCabinetFallbackTexture(state, 1024, 512);
  const sideArtMap = createSideArtTexture(state, 512, 512);
  const playfieldMap = createPlayfieldTexture(state, 1024, 512);
  const roughnessMap = createRoughnessTexture(state, mapSize, 0x9032af, 0.52, 0.22);
  const metalNormalMap = createNormalTexture(state, mapSize, 0x5a31ce, 1.35);
  const brushedRoughness = createDirectionalRoughnessTexture(state, mapSize, 0x8d9201);

  const blackPaint = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#15151b',
      map: paintMap,
      roughness: 0.5,
      roughnessMap,
      metalness: 0.58,
      normalMap: metalNormalMap,
      normalScale: new THREE.Vector2(0.2, 0.2),
      clearcoat: 0.34,
      clearcoatRoughness: 0.26,
      envMapIntensity: 0.95,
    }),
  );
  blackPaint.name = 'machine.material.blackPaint';

  const chrome = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#d1d5d9',
      roughness: 0.16,
      metalness: 1,
      clearcoat: 0.35,
      clearcoatRoughness: 0.08,
      envMapIntensity: 1.45,
    }),
  );
  chrome.name = 'machine.material.chrome';

  const brushedSteel = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#aeb4b8',
      roughness: 0.27,
      roughnessMap: brushedRoughness,
      metalness: 0.94,
      normalMap: metalNormalMap,
      normalScale: new THREE.Vector2(0.12, 0.35),
      anisotropy: 0.72,
      anisotropyRotation: 0,
      envMapIntensity: 1.35,
    }),
  );
  brushedSteel.name = 'machine.material.brushedSteel';

  const glass = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#a9dce7',
      roughness: 0.045,
      metalness: 0,
      transparent: true,
      opacity: 0.24,
      transmission: 0,
      thickness: 0.08,
      ior: 1.5,
      clearcoat: 1,
      clearcoatRoughness: 0.03,
      envMapIntensity: 1.55,
      depthWrite: false,
      side: THREE.DoubleSide,
      forceSinglePass: true,
    }),
  );
  glass.name = 'machine.material.glass';

  const cabinetArt = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      map: cabinetFallback,
      roughness: 0.29,
      roughnessMap,
      metalness: 0,
      normalMap: metalNormalMap,
      normalScale: new THREE.Vector2(0.035, 0.035),
      clearcoat: 0.58,
      clearcoatRoughness: 0.17,
      envMapIntensity: 0.82,
      polygonOffset: true,
      polygonOffsetFactor: -1,
    }),
  );
  cabinetArt.name = 'machine.material.cabinetArt';

  const sideArt = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      map: sideArtMap,
      roughness: 0.3,
      clearcoat: 0.5,
      clearcoatRoughness: 0.14,
      side: THREE.DoubleSide,
    }),
  );
  sideArt.name = 'machine.material.sideArt';

  const marquee = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      map: marqueeMap,
      roughness: 0.28,
      clearcoat: 0.48,
      clearcoatRoughness: 0.1,
      emissive: '#47112f',
      emissiveIntensity: 0.28,
      emissiveMap: marqueeMap,
    }),
  );
  marquee.name = 'machine.material.marquee';

  const playfield = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      map: playfieldMap,
      roughness: 0.42,
      metalness: 0.32,
      clearcoat: 0.2,
      clearcoatRoughness: 0.18,
    }),
  );
  playfield.name = 'machine.material.playfield';

  const nickel = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#c5c3ba',
      roughness: 0.23,
      metalness: 1,
      normalMap: metalNormalMap,
      normalScale: new THREE.Vector2(0.06, 0.06),
      envMapIntensity: 1.2,
    }),
  );
  nickel.name = 'machine.material.nickel';

  const brass = registerMaterial(
    state,
    new THREE.MeshPhysicalMaterial({
      color: '#d9a842',
      roughness: 0.2,
      metalness: 1,
      envMapIntensity: 1.15,
    }),
  );
  brass.name = 'machine.material.brass';

  const prizeColors: Array<[string, string, string]> = [
    ['#ed2d91', '#ff93cb', '20'],
    ['#ff8f25', '#ffd05d', '50'],
    ['#44c95f', '#b6f06e', '75'],
    ['#22c6df', '#a4f0ef', '100'],
  ];
  const prize = prizeColors.map(([base, accent, label], index) => {
    const texture = createPrizeTexture(state, 512, base, accent, label, index);
    const material = registerMaterial(
      state,
      new THREE.MeshPhysicalMaterial({
        color: '#ffffff',
        map: texture,
        roughness: 0.18,
        clearcoat: 0.78,
        clearcoatRoughness: 0.08,
      }),
    );
    material.name = `machine.material.prize.${label}`;
    return material;
  });

  const bulbWarm = createBulbMaterial(state, '#ff9c24', '#ff7314', 2.3, 'warm');
  const bulbPink = createBulbMaterial(state, '#ff4a9e', '#ff167d', 2.05, 'pink');
  const bulbCyan = createBulbMaterial(state, '#31c7ef', '#078ab3', 1.8, 'cyan');

  const rubber = registerMaterial(
    state,
    new THREE.MeshStandardMaterial({
      color: '#17171a',
      roughness: 0.86,
      metalness: 0.02,
      normalMap: metalNormalMap,
      normalScale: new THREE.Vector2(0.18, 0.18),
    }),
  );
  rubber.name = 'machine.material.rubber';

  const darkInterior = registerMaterial(
    state,
    new THREE.MeshStandardMaterial({
      color: '#09090d',
      roughness: 0.74,
      metalness: 0.22,
    }),
  );
  darkInterior.name = 'machine.material.darkInterior';

  const glowRed = registerMaterial(
    state,
    new THREE.MeshStandardMaterial({
      color: '#ff315f',
      emissive: '#e80d43',
      emissiveIntensity: 2.1,
      roughness: 0.24,
    }),
  );
  glowRed.name = 'machine.material.glowRed';

  const glowCyan = registerMaterial(
    state,
    new THREE.MeshStandardMaterial({
      color: '#31d7e9',
      emissive: '#079fb6',
      emissiveIntensity: 1.75,
      roughness: 0.24,
    }),
  );
  glowCyan.name = 'machine.material.glowCyan';

  return {
    blackPaint,
    chrome,
    brushedSteel,
    glass,
    cabinetArt,
    sideArt,
    marquee,
    playfield,
    nickel,
    brass,
    prize,
    bulbWarm,
    bulbPink,
    bulbCyan,
    rubber,
    darkInterior,
    glowRed,
    glowCyan,
  };
}

function buildRunningBase(context: BuildContext): void {
  const base = createNode(context, 'base.plinth', context.root);
  const plinth = createBoxMesh(context, 'base.plinth.mesh', [4.62, 0.42, 2.72], context.materials.blackPaint);
  plinth.position.set(0, 0.38, -0.06);
  base.add(plinth);

  const wheelGeometry = registerGeometry(context.state, new THREE.CylinderGeometry(0.17, 0.17, 0.14, 16));
  const yokeGeometry = registerGeometry(context.state, new THREE.BoxGeometry(0.18, 0.18, 0.13));
  const wheelPositions: ReadonlyArray<readonly [number, number, number]> = [
    [-1.7, 0.18, 0.93],
    [1.7, 0.18, 0.93],
    [-1.7, 0.18, -0.93],
    [1.7, 0.18, -0.93],
  ];
  wheelPositions.forEach(([x, y, z], index) => {
    const caster = createNode(context, `caster.${index}`, base);
    caster.position.set(x, y, z);
    const wheel = new THREE.Mesh(wheelGeometry, context.materials.rubber);
    wheel.rotation.z = Math.PI / 2;
    wheel.castShadow = true;
    wheel.receiveShadow = true;
    caster.add(wheel);
    context.meshes[`caster.${index}.wheel`] = wheel;

    const yoke = new THREE.Mesh(yokeGeometry, context.materials.chrome);
    yoke.position.y = 0.16;
    yoke.castShadow = true;
    caster.add(yoke);
    context.meshes[`caster.${index}.yoke`] = yoke;
  });
}

function buildLowerCabinet(context: BuildContext, options: CoinPusherMachineOptions): void {
  const cabinet = createNode(context, 'cabinet.lower', context.root);
  const shellGeometry = registerGeometry(context.state, createTrapezoidPrismGeometry(4.5, 5.12, 1.52, 3.08));
  const shell = new THREE.Mesh(shellGeometry, context.materials.blackPaint);
  shell.name = 'cabinet.lower.shell';
  shell.position.y = 0.48;
  shell.castShadow = true;
  shell.receiveShadow = true;
  cabinet.add(shell);
  context.meshes['cabinet.lower.shell'] = shell;

  const artBackingGeometry = registerGeometry(context.state, createTrapezoidPanelGeometry(4.18, 5.02, 1.32));
  const artBacking = new THREE.Mesh(artBackingGeometry, context.materials.chrome);
  artBacking.name = 'cabinet.art.front.backing';
  artBacking.position.set(0, 0.62, 1.553);
  artBacking.castShadow = true;
  cabinet.add(artBacking);
  context.meshes['cabinet.art.front.backing'] = artBacking;

  const frontArtGeometry = registerGeometry(context.state, createTrapezoidPanelGeometry(3.96, 4.76, 1.18));
  const frontArt = new THREE.Mesh(frontArtGeometry, context.materials.cabinetArt);
  frontArt.name = 'cabinet.art.front';
  frontArt.position.set(0, 0.7, 1.586);
  frontArt.castShadow = true;
  cabinet.add(frontArt);
  context.meshes['cabinet.art.front'] = frontArt;

  const artCorners = [
    new THREE.Vector3(-1.98, 0.7, 1.62),
    new THREE.Vector3(1.98, 0.7, 1.62),
    new THREE.Vector3(2.38, 1.88, 1.62),
    new THREE.Vector3(-2.38, 1.88, 1.62),
  ] as const;
  addBeam(context, 'cabinet.art.border.bottom', artCorners[0], artCorners[1], 0.065, 0.09, context.materials.chrome, cabinet);
  addBeam(context, 'cabinet.art.border.right', artCorners[1], artCorners[2], 0.065, 0.09, context.materials.chrome, cabinet);
  addBeam(context, 'cabinet.art.border.top', artCorners[2], artCorners[3], 0.065, 0.09, context.materials.chrome, cabinet);
  addBeam(context, 'cabinet.art.border.left', artCorners[3], artCorners[0], 0.065, 0.09, context.materials.chrome, cabinet);

  const sideBackingGeometry = registerGeometry(context.state, new THREE.PlaneGeometry(1.88, 1.38));
  const sideGeometry = registerGeometry(context.state, new THREE.PlaneGeometry(1.62, 1.16));
  for (const side of [-1, 1] as const) {
    const rotationY = side * 1.02;
    const normal = new THREE.Vector3(0, 0, 1).applyAxisAngle(Y_AXIS, rotationY);
    const backing = new THREE.Mesh(sideBackingGeometry, context.materials.chrome);
    backing.name = side < 0 ? 'cabinet.art.left.backing' : 'cabinet.art.right.backing';
    backing.position.set(side * 2.34, 1.29, 0.68).addScaledVector(normal, -0.035);
    backing.rotation.y = rotationY;
    backing.castShadow = true;
    cabinet.add(backing);
    context.meshes[backing.name] = backing;
  }

  const leftArt = new THREE.Mesh(sideGeometry, context.materials.sideArt);
  leftArt.name = 'cabinet.art.left';
  leftArt.position.set(-2.34, 1.29, 0.68);
  leftArt.rotation.y = -1.02;
  leftArt.castShadow = true;
  cabinet.add(leftArt);
  context.meshes['cabinet.art.left'] = leftArt;

  const rightArt = new THREE.Mesh(sideGeometry, context.materials.sideArt);
  rightArt.name = 'cabinet.art.right';
  rightArt.position.set(2.34, 1.29, 0.68);
  rightArt.rotation.y = 1.02;
  rightArt.castShadow = true;
  cabinet.add(rightArt);
  context.meshes['cabinet.art.right'] = rightArt;

  const tray = createNode(context, 'tray.collection', cabinet);
  const trayInterior = registerMaterial(context.state, context.materials.darkInterior.clone());
  trayInterior.name = 'machine.material.trayInterior';
  if (trayInterior instanceof THREE.MeshStandardMaterial) {
    trayInterior.color.set('#182028');
    trayInterior.roughness = 0.48;
    trayInterior.emissive.set('#351609');
    trayInterior.emissiveIntensity = 0.18;
  }
  const trayMetal = registerMaterial(context.state, context.materials.brushedSteel.clone());
  trayMetal.name = 'machine.material.trayMetal';
  if (trayMetal instanceof THREE.MeshStandardMaterial) {
    trayMetal.color.set('#71818b');
    trayMetal.metalness = 0.72;
    trayMetal.roughness = 0.3;
    trayMetal.emissive.set('#1d0d05');
    trayMetal.emissiveIntensity = 0.12;
  }
  const cavity = createBoxMesh(context, 'tray.cavity', [2.06, 0.48, 0.1], trayInterior);
  cavity.position.set(0, 1.02, 1.54);
  tray.add(cavity);
  const trayFloor = createBoxMesh(context, 'tray.floor', [1.84, 0.075, 0.62], trayMetal);
  trayFloor.position.set(0, 0.83, 1.83);
  trayFloor.rotation.x = -0.12;
  tray.add(trayFloor);
  for (const side of [-1, 1] as const) {
    const wall = createBoxMesh(
      context,
      side < 0 ? 'tray.wall.left' : 'tray.wall.right',
      [0.09, 0.38, 0.62],
      trayMetal,
    );
    wall.position.set(side * 0.96, 0.99, 1.82);
    tray.add(wall);
  }
  const trayHood = createBoxMesh(context, 'tray.hood', [2.02, 0.09, 0.24], context.materials.chrome);
  trayHood.position.set(0, 1.24, 1.64);
  tray.add(trayHood);
  const trayGlow = createBoxMesh(context, 'tray.innerGlow', [1.72, 0.025, 0.035], context.materials.bulbWarm);
  trayGlow.position.set(0, 1.18, 1.64);
  trayGlow.castShadow = false;
  tray.add(trayGlow);
  const trayPractical = new THREE.PointLight('#ffae62', 0.46, 2.2, 2);
  trayPractical.name = 'tray.practicalLight';
  trayPractical.position.set(0, 1.18, 1.92);
  trayPractical.castShadow = false;
  tray.add(trayPractical);
  const trayLip = createBoxMesh(context, 'tray.lip', [2.02, 0.1, 0.1], context.materials.chrome);
  trayLip.position.set(0, 0.78, 2.1);
  tray.add(trayLip);

  const collectionSocket = createSocket(context, 'socket.tray.collection', tray, [0, 0.94, 1.76]);
  collectionSocket.userData.event = 'coin:collect';

  const serviceDoor = createBoxMesh(context, 'cabinet.serviceDoor', [3.4, 1.16, 0.055], context.materials.blackPaint);
  serviceDoor.position.set(0, 1.22, -1.57);
  cabinet.add(serviceDoor);
  context.nodes['cabinet.serviceDoor'] = serviceDoor;

  for (const side of [-1, 1] as const) {
    const podId = side < 0 ? 'controls.left' : 'controls.right';
    const pod = createNode(context, podId, cabinet);
    pod.position.set(side * 2.53, 1.32, 0.5);
    pod.rotation.y = side * 0.2;
    const housing = createBoxMesh(context, `${podId}.housing`, [0.34, 0.68, 0.48], context.materials.blackPaint);
    housing.castShadow = true;
    pod.add(housing);
    const plate = createBoxMesh(context, `${podId}.plate`, [0.22, 0.34, 0.025], context.materials.chrome);
    plate.position.z = 0.255;
    pod.add(plate);
    const slot = createBoxMesh(context, `${podId}.slot`, [0.1, 0.025, 0.012], context.materials.darkInterior);
    slot.position.set(0, 0.07, 0.273);
    pod.add(slot);
  }

  if (options.detailLevel !== 'performance') {
    addCabinetFasteners(context, cabinet);
  }
}

function buildPlayfield(context: BuildContext, requestedCoinScale: number): void {
  const playfield = createNode(context, 'playfield.root', context.root);

  const apron = createBoxMesh(context, 'playfield.apron', [4.48, 0.34, 0.62], context.materials.blackPaint);
  apron.position.set(0, 1.91, 1.12);
  apron.rotation.x = -0.08;
  playfield.add(apron);

  const nameplateGeometry = registerGeometry(context.state, new THREE.PlaneGeometry(2.55, 0.26));
  const nameplate = new THREE.Mesh(nameplateGeometry, context.materials.marquee);
  nameplate.name = 'playfield.nameplate';
  nameplate.position.set(0, 1.98, 1.437);
  nameplate.rotation.x = -0.08;
  playfield.add(nameplate);
  context.meshes['playfield.nameplate'] = nameplate;

  const fixedBedNode = createNode(context, 'playfield.fixedBed', playfield);
  const fixedBed = createBoxMesh(context, 'playfield.fixedBed.mesh', [3.74, 0.12, 0.98], context.materials.brushedSteel);
  fixedBed.position.set(0, 2.13, 0.41);
  fixedBedNode.add(fixedBed);

  const laneGeometry = registerGeometry(context.state, new THREE.PlaneGeometry(0.52, 0.92));
  for (const side of [-1, 1] as const) {
    const lane = new THREE.Mesh(laneGeometry, context.materials.playfield);
    lane.name = side < 0 ? 'playfield.lane.left' : 'playfield.lane.right';
    lane.rotation.x = -Math.PI / 2;
    lane.position.set(side * 1.7, 2.198, 0.41);
    playfield.add(lane);
    context.meshes[lane.name] = lane;
  }

  for (const side of [-1, 1] as const) {
    const gutterId = side < 0 ? 'playfield.gutter.left' : 'playfield.gutter.right';
    const gutter = createBoxMesh(context, gutterId, [0.22, 0.18, 0.82], context.materials.darkInterior);
    gutter.position.set(side * 1.91, 2.16, 0.34);
    playfield.add(gutter);
    const glow = createBoxMesh(
      context,
      `${gutterId}.glow`,
      [0.035, 0.035, 0.72],
      side < 0 ? context.materials.glowCyan : context.materials.glowRed,
    );
    glow.position.set(side * 1.78, 2.23, 0.32);
    playfield.add(glow);
  }

  const pusher = createNode(context, 'pusher.pivot', playfield);
  pusher.position.set(0, 2.34, -0.15);
  const shelf = createBoxMesh(context, 'pusher.shelf', [3.64, 0.15, 0.5], context.materials.brushedSteel);
  shelf.castShadow = true;
  shelf.receiveShadow = true;
  pusher.add(shelf);
  const shelfLip = createBoxMesh(context, 'pusher.frontLip', [3.72, 0.13, 0.11], context.materials.chrome);
  shelfLip.position.set(0, 0.07, 0.285);
  pusher.add(shelfLip);
  addPusherComb(context, pusher);

  const backboardNode = createNode(context, 'playfield.backboard', playfield);
  const backboard = createBoxMesh(context, 'playfield.backboard.shell', [3.22, 1.42, 0.12], context.materials.blackPaint);
  backboard.position.set(0, 3.34, -1.04);
  backboardNode.add(backboard);
  const backboardFaceGeometry = registerGeometry(context.state, new THREE.PlaneGeometry(3.05, 1.25));
  const backboardFace = new THREE.Mesh(backboardFaceGeometry, context.materials.marquee);
  backboardFace.name = 'playfield.backboard.face';
  backboardFace.position.set(0, 3.34, -0.974);
  backboardNode.add(backboardFace);
  context.meshes['playfield.backboard.face'] = backboardFace;
  const dropRingGeometry = registerGeometry(context.state, new THREE.TorusGeometry(0.18, 0.025, 8, 24));
  const dropRing = new THREE.Mesh(dropRingGeometry, context.materials.chrome);
  dropRing.name = 'playfield.dropRing';
  dropRing.position.set(0, 3.63, -0.89);
  backboardNode.add(dropRing);
  context.meshes['playfield.dropRing'] = dropRing;

  const countScale = THREE.MathUtils.clamp(requestedCoinScale, 0.2, 1.8) * context.detailMultiplier;
  addCoinField(context, fixedBedNode, 'coins.fixed', Math.round(108 * countScale), 1.68, 0.36, 2.215, 0.4, context.seed ^ 0x9982);
  addCoinField(context, pusher, 'coins.pusher', Math.round(38 * countScale), 1.48, 0.14, 0.16, 0, context.seed ^ 0x32f1);
  addPrizeTiles(context, fixedBedNode);

  createSocket(context, 'socket.coinDrop.left', playfield, [-0.92, 3.72, -0.55]).userData.event = 'coin:drop';
  createSocket(context, 'socket.coinDrop.center', playfield, [0, 3.72, -0.55]).userData.event = 'coin:drop';
  createSocket(context, 'socket.coinDrop.right', playfield, [0.92, 3.72, -0.55]).userData.event = 'coin:drop';
  createSocket(context, 'socket.loss.left', playfield, [-1.84, 2.18, 0.14]).userData.event = 'coin:lost';
  createSocket(context, 'socket.loss.right', playfield, [1.84, 2.18, 0.14]).userData.event = 'coin:lost';
  createSocket(context, 'socket.payout.edge', playfield, [0, 1.86, 0.98]).userData.event = 'coin:collect';
  createSocket(context, 'socket.pusher.origin', pusher, [0, 0, 0]);
}

function buildGlazingAndFrame(context: BuildContext): void {
  const bay = createNode(context, 'cabinet.glassBay', context.root);
  const frontBottomLeft = new THREE.Vector3(-1.9, 1.78, 1.5);
  const frontBottomRight = new THREE.Vector3(1.9, 1.78, 1.5);
  const frontTopRight = new THREE.Vector3(1.52, 4.34, 0.76);
  const frontTopLeft = new THREE.Vector3(-1.52, 4.34, 0.76);
  const leftOuterBottom = new THREE.Vector3(-2.58, 1.75, 0.26);
  const leftOuterTop = new THREE.Vector3(-2.26, 4.31, -0.38);
  const rightOuterBottom = new THREE.Vector3(2.58, 1.75, 0.26);
  const rightOuterTop = new THREE.Vector3(2.26, 4.31, -0.38);

  addQuadMesh(context, 'glass.front', frontBottomLeft, frontBottomRight, frontTopRight, frontTopLeft, context.materials.glass, bay);
  addQuadMesh(context, 'glass.left', leftOuterBottom, frontBottomLeft, frontTopLeft, leftOuterTop, context.materials.glass, bay);
  addQuadMesh(context, 'glass.right', frontBottomRight, rightOuterBottom, rightOuterTop, frontTopRight, context.materials.glass, bay);

  const rails: ReadonlyArray<readonly [string, THREE.Vector3, THREE.Vector3]> = [
    ['frame.front.bottom', frontBottomLeft, frontBottomRight],
    ['frame.front.top', frontTopLeft, frontTopRight],
    ['frame.front.left', frontBottomLeft, frontTopLeft],
    ['frame.front.right', frontBottomRight, frontTopRight],
    ['frame.left.outer', leftOuterBottom, leftOuterTop],
    ['frame.left.bottom', leftOuterBottom, frontBottomLeft],
    ['frame.left.top', leftOuterTop, frontTopLeft],
    ['frame.right.outer', rightOuterBottom, rightOuterTop],
    ['frame.right.bottom', frontBottomRight, rightOuterBottom],
    ['frame.right.top', frontTopRight, rightOuterTop],
  ];
  for (const [id, start, end] of rails) {
    addBeam(context, id, start, end, 0.09, 0.12, context.materials.chrome, bay);
  }

  const glassCue = registerMaterial(context.state, context.materials.glass.clone());
  glassCue.name = 'machine.material.glassCue';
  glassCue.color.set('#d9fbff');
  glassCue.opacity = 0.5;
  glassCue.transmission = 0.18;
  glassCue.thickness = 0.12;
  glassCue.roughness = 0.09;
  glassCue.envMapIntensity = 1.8;
  const reflectionCues: ReadonlyArray<readonly [string, THREE.Vector3, THREE.Vector3]> = [
    ['glass.highlight.front.left', new THREE.Vector3(-1.24, 2.02, 1.43), new THREE.Vector3(-1.03, 4.09, 0.83)],
    ['glass.highlight.front.right', new THREE.Vector3(1.13, 2.12, 1.4), new THREE.Vector3(1.23, 3.18, 1.09)],
    ['glass.highlight.left', new THREE.Vector3(-2.31, 1.98, 0.39), new THREE.Vector3(-2.06, 3.88, -0.08)],
    ['glass.highlight.right', new THREE.Vector3(2.38, 2.06, 0.4), new THREE.Vector3(2.15, 3.7, -0.01)],
  ];
  for (const [id, start, end] of reflectionCues) {
    const cue = addBeam(context, id, start, end, 0.018, 0.016, glassCue, bay);
    cue.castShadow = false;
    cue.receiveShadow = false;
    cue.renderOrder = 12;
  }

  const leftWall = createBoxMesh(context, 'playfield.sideWall.left', [0.13, 0.92, 2.1], context.materials.blackPaint);
  leftWall.position.set(-2.19, 2.47, 0.1);
  leftWall.rotation.z = -0.08;
  bay.add(leftWall);
  const rightWall = createBoxMesh(context, 'playfield.sideWall.right', [0.13, 0.92, 2.1], context.materials.blackPaint);
  rightWall.position.set(2.19, 2.47, 0.1);
  rightWall.rotation.z = 0.08;
  bay.add(rightWall);
}

function buildMarquee(context: BuildContext): void {
  const marquee = createNode(context, 'marquee.body', context.root);
  const body = createBoxMesh(context, 'marquee.body.mesh', [4.48, 0.76, 2.18], context.materials.blackPaint);
  body.position.set(0, 4.94, -0.27);
  body.castShadow = true;
  marquee.add(body);

  const faceGeometry = registerGeometry(context.state, new THREE.PlaneGeometry(3.9, 0.52));
  const face = new THREE.Mesh(faceGeometry, context.materials.marquee);
  face.name = 'marquee.face';
  face.position.set(0, 4.96, 0.835);
  face.castShadow = true;
  marquee.add(face);
  context.meshes['marquee.face'] = face;
  context.nodes['marquee.face'] = face;

  const cap = createBoxMesh(context, 'marquee.cap', [4.72, 0.12, 2.34], context.materials.chrome);
  cap.position.set(0, 5.38, -0.28);
  marquee.add(cap);

  for (const side of [-1, 1] as const) {
    const wing = createBoxMesh(context, side < 0 ? 'marquee.wing.left' : 'marquee.wing.right', [1.38, 0.6, 0.08], context.materials.marquee);
    wing.position.set(side * 2.35, 4.94, 0.32);
    wing.rotation.y = side * 0.42;
    marquee.add(wing);
  }
}

function buildBulbRibbons(context: BuildContext): void {
  const bulbRoot = createNode(context, 'bulbs.root', context.root);
  const positions: THREE.Vector3[] = [];

  const topCount = Math.max(18, Math.round(27 * context.detailMultiplier));
  for (let index = 0; index < topCount; index += 1) {
    const t = topCount === 1 ? 0.5 : index / (topCount - 1);
    positions.push(new THREE.Vector3(THREE.MathUtils.lerp(-2.17, 2.17, t), 5.31, 0.89));
  }

  const railSegments: ReadonlyArray<readonly [THREE.Vector3, THREE.Vector3]> = [
    [new THREE.Vector3(-1.9, 1.84, 1.53), new THREE.Vector3(-1.52, 4.3, 0.79)],
    [new THREE.Vector3(1.9, 1.84, 1.53), new THREE.Vector3(1.52, 4.3, 0.79)],
    [new THREE.Vector3(-2.57, 1.82, 0.3), new THREE.Vector3(-2.25, 4.27, -0.34)],
    [new THREE.Vector3(2.57, 1.82, 0.3), new THREE.Vector3(2.25, 4.27, -0.34)],
  ];
  const railCount = Math.max(8, Math.round(13 * context.detailMultiplier));
  for (const [start, end] of railSegments) {
    for (let index = 0; index < railCount; index += 1) {
      const t = railCount === 1 ? 0.5 : index / (railCount - 1);
      positions.push(start.clone().lerp(end, t));
    }
  }

  const warm: THREE.Vector3[] = [];
  const pink: THREE.Vector3[] = [];
  const cyan: THREE.Vector3[] = [];
  positions.forEach((position, index) => {
    if (index % 7 === 3) cyan.push(position);
    else if (index % 3 === 1) pink.push(position);
    else warm.push(position);
  });

  addBulbInstances(context, 'bulbs.warm', warm, context.materials.bulbWarm, bulbRoot);
  addBulbInstances(context, 'bulbs.pink', pink, context.materials.bulbPink, bulbRoot);
  addBulbInstances(context, 'bulbs.cyan', cyan, context.materials.bulbCyan, bulbRoot);
  context.nodes['bulbs.top'] = bulbRoot;
}

function addPusherComb(context: BuildContext, parent: THREE.Object3D): void {
  const count = 20;
  const geometry = registerGeometry(context.state, new THREE.BoxGeometry(0.11, 0.16, 0.075));
  const teeth = new THREE.InstancedMesh(geometry, context.materials.chrome, count);
  teeth.name = 'pusher.comb';
  const dummy = new THREE.Object3D();
  for (let index = 0; index < count; index += 1) {
    dummy.position.set(THREE.MathUtils.lerp(-1.65, 1.65, index / (count - 1)), -0.055, 0.29);
    dummy.rotation.z = index % 2 === 0 ? -0.01 : 0.01;
    dummy.updateMatrix();
    teeth.setMatrixAt(index, dummy.matrix);
  }
  teeth.instanceMatrix.needsUpdate = true;
  teeth.castShadow = true;
  teeth.receiveShadow = true;
  parent.add(teeth);
  context.meshes['pusher.comb'] = teeth;
}

function addCoinField(
  context: BuildContext,
  parent: THREE.Object3D,
  idPrefix: string,
  count: number,
  halfWidth: number,
  halfDepth: number,
  baseY: number,
  centerZ: number,
  seed: number,
): void {
  const random = createRandom(seed);
  const nickelTransforms: THREE.Matrix4[] = [];
  const brassTransforms: THREE.Matrix4[] = [];
  const dummy = new THREE.Object3D();
  const columns = Math.max(5, Math.ceil(Math.sqrt(count * (halfWidth / Math.max(halfDepth, 0.1)))));
  const rows = Math.max(3, Math.ceil(count / columns));

  for (let index = 0; index < count; index += 1) {
    const column = index % columns;
    const row = Math.floor(index / columns) % rows;
    const x = THREE.MathUtils.lerp(-halfWidth, halfWidth, columns === 1 ? 0.5 : column / (columns - 1)) + (random() - 0.5) * 0.18;
    const z = centerZ + THREE.MathUtils.lerp(-halfDepth, halfDepth, rows === 1 ? 0.5 : row / (rows - 1)) + (random() - 0.5) * 0.19;
    dummy.position.set(x, baseY + (index % 3) * 0.022 + random() * 0.009, z);
    dummy.rotation.set((random() - 0.5) * 0.06, random() * Math.PI * 2, (random() - 0.5) * 0.06);
    const scale = 0.9 + random() * 0.18;
    dummy.scale.set(scale, 1, scale);
    dummy.updateMatrix();
    (random() < 0.17 ? brassTransforms : nickelTransforms).push(dummy.matrix.clone());
  }

  const geometry = registerGeometry(context.state, new THREE.CylinderGeometry(0.122, 0.122, 0.028, context.detailMultiplier < 1 ? 12 : 18));
  addTransformInstances(context, `${idPrefix}.nickel`, geometry, context.materials.nickel, nickelTransforms, parent);
  addTransformInstances(context, `${idPrefix}.brass`, geometry, context.materials.brass, brassTransforms, parent);
}

function addPrizeTiles(context: BuildContext, parent: THREE.Object3D): void {
  const random = createRandom(context.seed ^ 0x81c7);
  const count = Math.max(8, Math.round(13 * context.detailMultiplier));
  for (let index = 0; index < count; index += 1) {
    const isDisc = index % 5 === 0;
    const geometry = isDisc
      ? registerGeometry(context.state, new THREE.CylinderGeometry(0.23, 0.23, 0.045, 28))
      : registerGeometry(context.state, createRoundedTileGeometry(0.48, 0.34, 0.055));
    const prize = new THREE.Mesh(geometry, context.materials.prize[index % context.materials.prize.length]);
    prize.name = `prize.${index}`;
    prize.position.set(-1.02 + random() * 2.2, 2.29 + (index % 3) * 0.035, 0.04 + random() * 0.6);
    if (!isDisc) prize.rotation.x = -Math.PI / 2;
    prize.rotation.y = (random() - 0.5) * 0.9;
    prize.rotation.z = isDisc ? 0 : (random() - 0.5) * 0.06;
    prize.castShadow = true;
    prize.receiveShadow = true;
    parent.add(prize);
    context.meshes[prize.name] = prize;
  }
}

function addCabinetFasteners(context: BuildContext, parent: THREE.Object3D): void {
  const geometry = registerGeometry(context.state, new THREE.SphereGeometry(0.035, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2));
  const positions: THREE.Vector3[] = [];
  for (const x of [-2.28, 2.28]) {
    for (const y of [0.72, 1.08, 1.44, 1.8]) positions.push(new THREE.Vector3(x, y, 1.3));
  }
  for (const x of [-1.7, -0.85, 0, 0.85, 1.7]) positions.push(new THREE.Vector3(x, 1.94, 1.5));
  const fasteners = new THREE.InstancedMesh(geometry, context.materials.chrome, positions.length);
  fasteners.name = 'cabinet.fasteners';
  const dummy = new THREE.Object3D();
  positions.forEach((position, index) => {
    dummy.position.copy(position);
    dummy.updateMatrix();
    fasteners.setMatrixAt(index, dummy.matrix);
  });
  fasteners.instanceMatrix.needsUpdate = true;
  parent.add(fasteners);
  context.meshes['cabinet.fasteners'] = fasteners;
}

function addBulbInstances(
  context: BuildContext,
  id: string,
  positions: readonly THREE.Vector3[],
  material: THREE.Material,
  parent: THREE.Object3D,
): void {
  if (positions.length === 0) return;
  const geometry = registerGeometry(context.state, new THREE.SphereGeometry(0.054, 12, 8));
  const bulbs = new THREE.InstancedMesh(geometry, material, positions.length);
  bulbs.name = id;
  const dummy = new THREE.Object3D();
  positions.forEach((position, index) => {
    dummy.position.copy(position);
    const scale = 0.94 + (index % 3) * 0.035;
    dummy.scale.setScalar(scale);
    dummy.updateMatrix();
    bulbs.setMatrixAt(index, dummy.matrix);
  });
  bulbs.instanceMatrix.needsUpdate = true;
  bulbs.castShadow = false;
  parent.add(bulbs);
  context.meshes[id] = bulbs;
  context.nodes[id] = bulbs;
}

function addTransformInstances(
  context: BuildContext,
  id: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  transforms: readonly THREE.Matrix4[],
  parent: THREE.Object3D,
): void {
  if (transforms.length === 0) return;
  const mesh = new THREE.InstancedMesh(geometry, material, transforms.length);
  mesh.name = id;
  transforms.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
  mesh.instanceMatrix.needsUpdate = true;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  context.meshes[id] = mesh;
}

function addQuadMesh(
  context: BuildContext,
  id: string,
  a: THREE.Vector3,
  b: THREE.Vector3,
  c: THREE.Vector3,
  d: THREE.Vector3,
  material: THREE.Material,
  parent: THREE.Object3D,
): THREE.Mesh {
  const geometry = registerGeometry(context.state, createQuadGeometry(a, b, c, d));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = id;
  mesh.renderOrder = 10;
  parent.add(mesh);
  context.meshes[id] = mesh;
  context.nodes[id] = mesh;
  return mesh;
}

function addBeam(
  context: BuildContext,
  id: string,
  start: THREE.Vector3,
  end: THREE.Vector3,
  width: number,
  depth: number,
  material: THREE.Material,
  parent: THREE.Object3D,
): THREE.Mesh {
  const direction = end.clone().sub(start);
  const geometry = registerGeometry(context.state, new THREE.BoxGeometry(width, direction.length(), depth));
  const beam = new THREE.Mesh(geometry, material);
  beam.name = id;
  beam.position.copy(start).add(end).multiplyScalar(0.5);
  beam.quaternion.setFromUnitVectors(Y_AXIS, direction.normalize());
  beam.castShadow = true;
  beam.receiveShadow = true;
  parent.add(beam);
  context.meshes[id] = beam;
  return beam;
}

function createNode(context: BuildContext, id: string, parent: THREE.Object3D): THREE.Group {
  const node = new THREE.Group();
  node.name = id;
  parent.add(node);
  context.nodes[id] = node;
  return node;
}

function createSocket(
  context: BuildContext,
  id: string,
  parent: THREE.Object3D,
  position: readonly [number, number, number],
): THREE.Object3D {
  const socket = new THREE.Object3D();
  socket.name = id;
  socket.position.set(...position);
  socket.userData.socket = true;
  parent.add(socket);
  context.sockets[id] = socket;
  context.nodes[id] = socket;
  return socket;
}

function createBoxMesh(
  context: BuildContext,
  id: string,
  size: readonly [number, number, number],
  material: THREE.Material,
): THREE.Mesh {
  const geometry = registerGeometry(context.state, new THREE.BoxGeometry(...size));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = id;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  context.meshes[id] = mesh;
  return mesh;
}

function createTrapezoidPrismGeometry(bottomWidth: number, topWidth: number, height: number, depth: number): THREE.BufferGeometry {
  const bottomHalf = bottomWidth / 2;
  const topHalf = topWidth / 2;
  const halfDepth = depth / 2;
  const positions = new Float32Array([
    -bottomHalf, 0, halfDepth,
    bottomHalf, 0, halfDepth,
    topHalf, height, halfDepth,
    -topHalf, height, halfDepth,
    -bottomHalf, 0, -halfDepth,
    bottomHalf, 0, -halfDepth,
    topHalf, height, -halfDepth,
    -topHalf, height, -halfDepth,
  ]);
  const indices = [
    0, 1, 2, 0, 2, 3,
    5, 4, 7, 5, 7, 6,
    4, 0, 3, 4, 3, 7,
    1, 5, 6, 1, 6, 2,
    3, 2, 6, 3, 6, 7,
    4, 5, 1, 4, 1, 0,
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createTrapezoidPanelGeometry(bottomWidth: number, topWidth: number, height: number): THREE.BufferGeometry {
  const positions = new Float32Array([
    -bottomWidth / 2, 0, 0,
    bottomWidth / 2, 0, 0,
    topWidth / 2, height, 0,
    -topWidth / 2, height, 0,
  ]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.computeVertexNormals();
  return geometry;
}

function createQuadGeometry(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([
      a.x, a.y, a.z,
      b.x, b.y, b.z,
      c.x, c.y, c.z,
      d.x, d.y, d.z,
    ], 3),
  );
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.computeVertexNormals();
  return geometry;
}

function createRoundedTileGeometry(width: number, height: number, radius: number): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  const x = -width / 2;
  const y = -height / 2;
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  return new THREE.ExtrudeGeometry(shape, {
    depth: 0.045,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: 0.012,
    bevelThickness: 0.012,
    curveSegments: 4,
  });
}

function applyCabinetArt(
  root: CoinPusherMachine,
  options: CoinPusherMachineOptions,
  cabinetArtUrl: string | null,
): void {
  const runtime = getCoinPusherMachineRuntime(root);
  const material = runtime.materials['cabinet-art'] as THREE.MeshPhysicalMaterial;
  if (options.cabinetArtTexture) {
    configureColorTexture(options.cabinetArtTexture);
    material.map = options.cabinetArtTexture;
    material.needsUpdate = true;
    return;
  }
  if (!cabinetArtUrl || typeof document === 'undefined') return;

  const state = resourceStates.get(root);
  const loader = options.textureLoader ?? new THREE.TextureLoader();
  loader.load(
    cabinetArtUrl,
    (texture) => {
      if (!state || state.disposed) {
        texture.dispose();
        return;
      }
      configureColorTexture(texture);
      texture.anisotropy = 8;
      state.textures.add(texture);
      material.map = texture;
      material.needsUpdate = true;
    },
    undefined,
    () => undefined,
  );
}

function createPaintTexture(state: ResourceState, width: number, seed: number): THREE.CanvasTexture {
  const canvas = createCanvas(width, width);
  const context = getCanvasContext(canvas);
  context.fillStyle = '#17171e';
  context.fillRect(0, 0, width, width);
  const random = createRandom(seed);
  for (let index = 0; index < 180; index += 1) {
    const alpha = 0.018 + random() * 0.035;
    context.strokeStyle = `rgba(190, 195, 204, ${alpha})`;
    context.lineWidth = 0.6 + random() * 1.5;
    context.beginPath();
    const x = random() * width;
    const y = random() * width;
    context.moveTo(x, y);
    context.lineTo(x + (random() - 0.5) * 55, y + (random() - 0.5) * 14);
    context.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas);
  configureColorTexture(texture);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 2);
  return registerTexture(state, texture);
}

function createCabinetFallbackTexture(state: ResourceState, width: number, height: number): THREE.CanvasTexture {
  const canvas = createCanvas(width, height);
  const context = getCanvasContext(canvas);
  const sky = context.createLinearGradient(0, 0, 0, height);
  sky.addColorStop(0, '#12072c');
  sky.addColorStop(0.42, '#9c146d');
  sky.addColorStop(0.72, '#ff7030');
  sky.addColorStop(1, '#080914');
  context.fillStyle = sky;
  context.fillRect(0, 0, width, height);

  context.fillStyle = '#0b1227';
  for (let index = 0; index < 24; index += 1) {
    const buildingWidth = 24 + (index % 5) * 7;
    const buildingHeight = 45 + ((index * 37) % 130);
    const x = index * (width / 23) - 20;
    context.fillRect(x, height - 74 - buildingHeight, buildingWidth, buildingHeight);
    context.fillStyle = index % 3 === 0 ? '#19bbd2' : '#ffad2f';
    for (let row = 0; row < 5; row += 1) {
      context.fillRect(x + 7, height - 92 - row * 22, 4, 8);
    }
    context.fillStyle = '#0b1227';
  }

  drawPalm(context, width * 0.12, height * 0.79, 0.9, '#080913');
  drawPalm(context, width * 0.86, height * 0.8, 1.05, '#080913');
  context.textAlign = 'center';
  context.font = `900 ${Math.round(height * 0.17)}px Arial Black, sans-serif`;
  context.lineWidth = Math.max(6, height * 0.018);
  context.strokeStyle = '#130822';
  context.strokeText('NIGHT PARADE', width / 2, height * 0.3);
  context.fillStyle = '#ff3aa8';
  context.fillText('NIGHT PARADE', width / 2, height * 0.3);
  context.font = `700 ${Math.round(height * 0.055)}px Arial, sans-serif`;
  context.fillStyle = '#ffe25c';
  context.fillText('LUCKY COIN CARNIVAL', width / 2, height * 0.39);

  context.strokeStyle = '#ffc23e';
  context.lineWidth = 9;
  context.strokeRect(13, 13, width - 26, height - 26);
  const texture = new THREE.CanvasTexture(canvas);
  configureColorTexture(texture);
  return registerTexture(state, texture);
}

function createSideArtTexture(state: ResourceState, width: number, height: number): THREE.CanvasTexture {
  const canvas = createCanvas(width, height);
  const context = getCanvasContext(canvas);
  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#090717');
  gradient.addColorStop(0.5, '#51114f');
  gradient.addColorStop(1, '#e02b91');
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
  context.strokeStyle = '#ffbd35';
  context.lineWidth = 12;
  context.strokeRect(18, 18, width - 36, height - 36);
  drawPalm(context, width * 0.2, height * 0.85, 0.75, '#060610');
  context.save();
  context.translate(width / 2, height / 2);
  context.rotate(-0.12);
  context.textAlign = 'center';
  context.font = `900 ${Math.round(height * 0.11)}px Arial Black, sans-serif`;
  context.fillStyle = '#5ee9f2';
  context.strokeStyle = '#231037';
  context.lineWidth = 8;
  context.strokeText('LUCKY COIN', 0, 0);
  context.fillText('LUCKY COIN', 0, 0);
  context.restore();
  const texture = new THREE.CanvasTexture(canvas);
  configureColorTexture(texture);
  return registerTexture(state, texture);
}

function createMarqueeTexture(state: ResourceState, width: number, height: number): THREE.CanvasTexture {
  const canvas = createCanvas(width, height);
  const context = getCanvasContext(canvas);
  const gradient = context.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0, '#0a0a12');
  gradient.addColorStop(0.5, '#28122f');
  gradient.addColorStop(1, '#0a0a12');
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
  context.strokeStyle = '#eaa52b';
  context.lineWidth = 8;
  context.strokeRect(10, 10, width - 20, height - 20);
  context.textAlign = 'center';
  context.font = `900 ${Math.round(height * 0.37)}px Arial Black, sans-serif`;
  context.strokeStyle = '#13081d';
  context.lineWidth = 12;
  context.strokeText('NIGHT PARADE', width / 2, height * 0.55);
  context.fillStyle = '#ff399d';
  context.fillText('NIGHT PARADE', width / 2, height * 0.55);
  context.font = `700 ${Math.round(height * 0.13)}px Arial, sans-serif`;
  context.fillStyle = '#48dfed';
  context.fillText('LUCKY COIN • BONUS DROP', width / 2, height * 0.78);
  for (const x of [0.08, 0.16, 0.84, 0.92]) {
    context.fillStyle = x < 0.5 ? '#ffc033' : '#45d6e8';
    context.beginPath();
    context.moveTo(width * x, height * 0.5);
    context.lineTo(width * (x + (x < 0.5 ? 0.045 : -0.045)), height * 0.36);
    context.lineTo(width * (x + (x < 0.5 ? 0.045 : -0.045)), height * 0.64);
    context.closePath();
    context.fill();
  }
  const texture = new THREE.CanvasTexture(canvas);
  configureColorTexture(texture);
  return registerTexture(state, texture);
}

function createPlayfieldTexture(state: ResourceState, width: number, height: number): THREE.CanvasTexture {
  const canvas = createCanvas(width, height);
  const context = getCanvasContext(canvas);
  context.fillStyle = '#2a2930';
  context.fillRect(0, 0, width, height);
  const colors = ['#d5a32c', '#72379d', '#1bb9d0', '#17151e'];
  const stripeWidth = width / 18;
  for (let index = -4; index < 24; index += 1) {
    context.fillStyle = colors[Math.abs(index) % colors.length];
    context.beginPath();
    context.moveTo(index * stripeWidth, 0);
    context.lineTo((index + 1) * stripeWidth, 0);
    context.lineTo((index - 2) * stripeWidth, height);
    context.lineTo((index - 3) * stripeWidth, height);
    context.closePath();
    context.fill();
  }
  context.fillStyle = 'rgba(13, 13, 18, 0.78)';
  context.fillRect(width * 0.22, height * 0.18, width * 0.56, height * 0.64);
  context.strokeStyle = '#ed3c9b';
  context.lineWidth = 8;
  context.strokeRect(width * 0.22, height * 0.18, width * 0.56, height * 0.64);
  context.fillStyle = '#f0d05b';
  context.font = `900 ${Math.round(height * 0.15)}px Arial Black, sans-serif`;
  context.textAlign = 'center';
  context.fillText('LUCKY COIN', width / 2, height * 0.58);
  const texture = new THREE.CanvasTexture(canvas);
  configureColorTexture(texture);
  return registerTexture(state, texture);
}

function createPrizeTexture(
  state: ResourceState,
  size: number,
  base: string,
  accent: string,
  label: string,
  variant: number,
): THREE.CanvasTexture {
  const canvas = createCanvas(size, size);
  const context = getCanvasContext(canvas);
  context.fillStyle = base;
  context.fillRect(0, 0, size, size);
  context.strokeStyle = accent;
  context.lineWidth = size * 0.055;
  context.strokeRect(size * 0.07, size * 0.07, size * 0.86, size * 0.86);
  context.save();
  context.translate(size / 2, size * 0.33);
  context.strokeStyle = '#ffd85d';
  context.lineWidth = size * 0.024;
  for (let ray = 0; ray < 12; ray += 1) {
    context.rotate(Math.PI / 6);
    context.beginPath();
    context.moveTo(0, 0);
    context.lineTo(0, size * 0.18);
    context.stroke();
  }
  context.restore();
  context.fillStyle = '#fff6df';
  context.textAlign = 'center';
  context.font = `900 ${Math.round(size * 0.25)}px Arial Black, sans-serif`;
  context.fillText(label, size / 2, size * 0.72);
  context.font = `700 ${Math.round(size * 0.07)}px Arial, sans-serif`;
  context.fillText(variant % 2 === 0 ? 'BONUS' : 'LUCKY', size / 2, size * 0.84);
  const texture = new THREE.CanvasTexture(canvas);
  configureColorTexture(texture);
  return registerTexture(state, texture);
}

function createRoughnessTexture(
  state: ResourceState,
  size: number,
  seed: number,
  base: number,
  variation: number,
): THREE.DataTexture {
  const random = createRandom(seed);
  const data = new Uint8Array(size * size * 4);
  for (let index = 0; index < size * size; index += 1) {
    const lowFrequency = Math.sin((index % size) * 0.07) * 0.11 + Math.cos(Math.floor(index / size) * 0.05) * 0.08;
    const value = THREE.MathUtils.clamp(base + lowFrequency * variation + (random() - 0.5) * variation, 0, 1);
    const byte = Math.round(value * 255);
    const offset = index * 4;
    data[offset] = byte;
    data[offset + 1] = byte;
    data[offset + 2] = byte;
    data[offset + 3] = 255;
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  configureDataTexture(texture);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  return registerTexture(state, texture);
}

function createDirectionalRoughnessTexture(state: ResourceState, size: number, seed: number): THREE.DataTexture {
  const random = createRandom(seed);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const streak = Math.sin(y * 0.58 + Math.sin(x * 0.035) * 1.5) * 0.09;
      const value = THREE.MathUtils.clamp(0.43 + streak + (random() - 0.5) * 0.1, 0, 1);
      const byte = Math.round(value * 255);
      const offset = (y * size + x) * 4;
      data[offset] = byte;
      data[offset + 1] = byte;
      data[offset + 2] = byte;
      data[offset + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  configureDataTexture(texture);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(3, 5);
  return registerTexture(state, texture);
}

function createNormalTexture(state: ResourceState, size: number, seed: number, strength: number): THREE.DataTexture {
  const random = createRandom(seed);
  const height = new Float32Array(size * size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      height[y * size + x] =
        Math.sin(x * 0.18) * 0.2 +
        Math.cos(y * 0.13) * 0.16 +
        (random() - 0.5) * 0.44;
    }
  }
  const data = new Uint8Array(size * size * 4);
  const sample = (x: number, y: number): number => height[((y + size) % size) * size + ((x + size) % size)];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = (sample(x + 1, y) - sample(x - 1, y)) * strength;
      const dy = (sample(x, y + 1) - sample(x, y - 1)) * strength;
      const normal = new THREE.Vector3(-dx, -dy, 1).normalize();
      const offset = (y * size + x) * 4;
      data[offset] = Math.round((normal.x * 0.5 + 0.5) * 255);
      data[offset + 1] = Math.round((normal.y * 0.5 + 0.5) * 255);
      data[offset + 2] = Math.round((normal.z * 0.5 + 0.5) * 255);
      data[offset + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  configureDataTexture(texture);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(5, 5);
  return registerTexture(state, texture);
}

function createBulbMaterial(
  state: ResourceState,
  color: string,
  emissive: string,
  emissiveIntensity: number,
  name: string,
): THREE.MeshStandardMaterial {
  const material = registerMaterial(
    state,
    new THREE.MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity,
      roughness: 0.18,
      metalness: 0.04,
    }),
  );
  material.name = `machine.material.bulb.${name}`;
  return material;
}

function drawPalm(
  context: CanvasRenderingContext2D,
  x: number,
  baseY: number,
  scale: number,
  color: string,
): void {
  context.save();
  context.strokeStyle = color;
  context.fillStyle = color;
  context.lineCap = 'round';
  context.lineWidth = 12 * scale;
  context.beginPath();
  context.moveTo(x, baseY);
  context.quadraticCurveTo(x - 8 * scale, baseY - 95 * scale, x + 9 * scale, baseY - 180 * scale);
  context.stroke();
  context.translate(x + 9 * scale, baseY - 180 * scale);
  for (let index = 0; index < 9; index += 1) {
    context.save();
    context.rotate((index / 9) * Math.PI * 2);
    context.beginPath();
    context.ellipse(0, -52 * scale, 14 * scale, 64 * scale, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }
  context.restore();
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function getCanvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create a 2D canvas context for coin-pusher textures.');
  return context;
}

function configureColorTexture(texture: THREE.Texture): void {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
}

function configureDataTexture(texture: THREE.DataTexture): void {
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
}

function registerGeometry<T extends THREE.BufferGeometry>(state: ResourceState, geometry: T): T {
  state.geometries.add(geometry);
  return geometry;
}

function registerMaterial<T extends THREE.Material>(state: ResourceState, material: T): T {
  state.materials.add(material);
  return material;
}

function registerTexture<T extends THREE.Texture>(state: ResourceState, texture: T): T {
  state.textures.add(texture);
  return texture;
}

function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}
